/**
 * Recommendation tracking in the chat. The record card lists exactly what will be recorded (the latest
 * analysis's Focal Points, marked when an earlier review raised the same finding, and recommendations the
 * person stated) and records it only on a click. The tracker is where people change status, owner and
 * due date; closing or reopening needs a note. The agent never changes a recommendation.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ClipboardDocumentCheckIcon, ArrowPathIcon, CheckIcon } from '@heroicons/react/24/outline';
import { agentApi, type ActionCardState, type RecommendationRow, type RecommendationStatus } from '../../lib/agent';
import { shell, primaryBtn, secondaryBtn, useRefresh } from './cardKit';

const STATUS_WORD: Record<RecommendationStatus, string> = {
  OPEN: 'Open', IN_PROGRESS: 'In progress', RESOLVED: 'Resolved', RISK_ACCEPTED: 'Risk accepted',
};
const CLOSED: RecommendationStatus[] = ['RESOLVED', 'RISK_ACCEPTED'];

function RepeatBadge({ text }: { text: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
      <ArrowPathIcon className="h-3 w-3" /> Repeat: {text}
    </span>
  );
}

export function RecordRecommendationsCard({ teamId, card, queryKey }: { teamId: string; card: ActionCardState; queryKey: unknown[] }) {
  const refresh = useRefresh(queryKey);
  const qc = useQueryClient();
  const preview = (card.params as { preview?: { title: string; detail: string | null; repeat: string | null }[] } | null)?.preview ?? [];
  const approve = useMutation({
    mutationFn: () => agentApi.approveAction(teamId, card.id).then((r) => r.data.card),
    onSuccess: async () => {
      await refresh();
      await qc.invalidateQueries({ queryKey: ['agent-recommendations', teamId] });
    },
    onError: refresh,
  });
  const reject = useMutation({ mutationFn: () => agentApi.rejectAction(teamId, card.id), onSuccess: refresh });
  const r = card.result as { recorded?: number; repeats?: number; skipped?: number } | null;

  return (
    <div className={clsx(shell, card.status === 'FAILED' ? 'border-red-200' : 'border-gray-200')}>
      <div className="flex items-start gap-3">
        <ClipboardDocumentCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-ocean-600" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-navy-900">{card.title}</p>
          <ul className="mt-2 space-y-1.5">
            {preview.map((p) => (
              <li key={p.title} className="text-xs text-gray-700">
                <span className="font-medium text-navy-900">{p.title}</span>
                {p.detail && <span className="text-gray-500"> · {p.detail}</span>}
                {p.repeat && <div className="mt-0.5"><RepeatBadge text={p.repeat} /></div>}
              </li>
            ))}
          </ul>
          {card.status === 'PROPOSED' && (
            <div className="mt-3 flex gap-2">
              <button type="button" className={primaryBtn} disabled={approve.isPending || reject.isPending} onClick={() => approve.mutate()}>
                <CheckIcon className="h-4 w-4" /> {approve.isPending ? 'Recording…' : 'Record'}
              </button>
              <button type="button" className={secondaryBtn} disabled={approve.isPending || reject.isPending} onClick={() => reject.mutate()}>Not now</button>
            </div>
          )}
          {card.status === 'DONE' && r && (
            <p className="mt-2 text-xs text-emerald-700">
              Recorded {r.recorded ?? 0}{r.repeats ? `, ${r.repeats} of them repeat findings` : ''}{r.skipped ? `; ${r.skipped} already recorded were left out` : ''}. Track them below or ask Clarid for the tracker.
            </p>
          )}
          {card.status === 'FAILED' && <p className="mt-1 text-xs text-red-700">{card.error ?? 'They could not be recorded.'}</p>}
          {card.status === 'REJECTED' && <p className="mt-1 text-xs text-gray-500">Not recorded.</p>}
        </div>
      </div>
      {card.status === 'DONE' && <div className="mt-3"><RecommendationsTracker teamId={teamId} includeClosed={false} embedded /></div>}
    </div>
  );
}

export function RecommendationsTracker({ teamId, includeClosed, embedded = false }: { teamId: string; includeClosed: boolean; embedded?: boolean }) {
  const { data: rows } = useQuery({
    queryKey: ['agent-recommendations', teamId, includeClosed],
    queryFn: () => agentApi.recommendations(teamId, includeClosed).then((r) => r.data.recommendations),
  });
  if (!rows) return null;
  const body = rows.length === 0
    ? <p className="text-xs text-gray-500">No recommendations are {includeClosed ? 'recorded' : 'open'}.</p>
    : <ul className="divide-y divide-gray-100">{rows.map((r) => <TrackerRow key={r.id} teamId={teamId} row={r} />)}</ul>;
  if (embedded) return body;
  return (
    <div className={`${shell} border-gray-200`}>
      <p className="mb-2 inline-flex items-center gap-1.5 font-medium text-navy-900">
        <ClipboardDocumentCheckIcon className="h-5 w-5 text-ocean-600" /> Recommendations{includeClosed ? '' : ' (open)'}
      </p>
      {body}
    </div>
  );
}

function TrackerRow({ teamId, row }: { teamId: string; row: RecommendationRow }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<RecommendationStatus>(row.status);
  const [ownerName, setOwnerName] = useState(row.ownerName ?? '');
  const [dueDate, setDueDate] = useState(row.dueDate ?? '');
  const [note, setNote] = useState('');
  const wasClosed = CLOSED.includes(row.status);
  const needsNote = status !== row.status && CLOSED.includes(status) !== wasClosed;
  const changed = status !== row.status || ownerName !== (row.ownerName ?? '') || dueDate !== (row.dueDate ?? '');
  const save = useMutation({
    mutationFn: () => agentApi.updateRecommendation(teamId, row.id, {
      ...(status !== row.status ? { status } : {}),
      ...(ownerName !== (row.ownerName ?? '') ? { ownerName } : {}),
      ...(dueDate !== (row.dueDate ?? '') ? { dueDate: dueDate || null } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    }),
    onSuccess: async () => {
      setNote('');
      await qc.invalidateQueries({ queryKey: ['agent-recommendations', teamId] });
      await qc.invalidateQueries({ queryKey: ['agent-home', teamId] });
    },
  });
  const input = 'rounded border border-gray-300 px-2 py-1 text-xs';

  return (
    <li className="py-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-navy-900">{row.title}</span>
        {row.reviewYear && <span className="text-gray-400">{row.reviewYear} review</span>}
        {row.overdue && <span className="rounded-full bg-red-50 px-2 py-0.5 text-red-700">Overdue</span>}
        {row.repeatOf && <RepeatBadge text={`raised in the ${row.repeatOf.reviewYear ?? 'earlier'} review`} />}
      </div>
      {row.detail && <p className="mt-0.5 text-gray-500">{row.detail}</p>}
      {row.closeNote && <p className="mt-0.5 text-gray-500">{STATUS_WORD[row.status]}: {row.closeNote}</p>}
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <select aria-label="Status" className={input} value={status} onChange={(e) => setStatus(e.target.value as RecommendationStatus)}>
          {(Object.keys(STATUS_WORD) as RecommendationStatus[]).map((s) => <option key={s} value={s}>{STATUS_WORD[s]}</option>)}
        </select>
        <input aria-label="Owner" className={`${input} w-36`} placeholder="Owner" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
        <input aria-label="Due date" type="date" className={input} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        {needsNote && (
          <input
            aria-label="Note"
            className={`${input} min-w-[14rem] flex-1`}
            placeholder={CLOSED.includes(status) ? (status === 'RESOLVED' ? 'How was it resolved?' : 'Why is the risk accepted?') : 'Why is it reopened?'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        )}
        {changed && (
          <button type="button" className={primaryBtn} disabled={save.isPending || (needsNote && !note.trim())} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </li>
  );
}
